/**
 * Shopify App Bridge bootstrap.
 *
 * App Bridge is loaded via the CDN script in index.html; it attaches a global
 * `shopify` object once the app is embedded in the admin iframe. We do NOT
 * use the deprecated @shopify/app-bridge package — only `@shopify/app-bridge-react`
 * components, which talk to the global `shopify` object provided by the CDN.
 *
 * The job of this module:
 *  1. Detect if we're embedded in Shopify admin (via `host` query param)
 *  2. Provide a typed helper to request a fresh session token for API calls
 *  3. Provide a helper for top-frame redirects (needed for the billing flow)
 */

export interface AppBridgeBootArgs {
  host: string;
  shop: string | null;
}

export function initAppBridge(args: AppBridgeBootArgs): void {
  // The CDN script reads the `shopify-api-key` <meta> tag and the URL's `host` param
  // to build the bridge automatically. There's nothing to call — we just verify presence.
  if (typeof window === "undefined") return;
  if (!window.shopify) {
    console.warn(
      "[brilio] App Bridge global `shopify` is not present. Make sure cdn.shopify.com/shopifycloud/app-bridge.js loaded before main.tsx.",
    );
    return;
  }
  if (import.meta.env.DEV) {
    console.debug("[brilio] App Bridge ready", {
      host: args.host,
      shop: args.shop,
    });
  }
}

/**
 * Returns a fresh Shopify session token (JWT). Use as a Bearer for Brilio API calls.
 * The token's `dest` claim is the shop URL; our backend resolves it to a ShopifyShop row.
 */
export async function getShopifySessionToken(): Promise<string | null> {
  if (!window.shopify) return null;
  try {
    return await window.shopify.idToken();
  } catch (err) {
    console.error("[brilio] Failed to acquire App Bridge session token", err);
    return null;
  }
}

/**
 * Top-frame redirect — needed when we send the merchant to Shopify's managed
 * pricing page. Inside the admin iframe a normal `window.location` is sandboxed.
 *
 * App Bridge v4 patches the global `open()`: a `_top` target is forwarded to the
 * host as a navigation redirect, which escapes the iframe. Outside Shopify this
 * is plain `window.open`, which navigates the current top-level window. One call
 * is correct in both places — and there is no branch left to get wrong.
 *
 * Do NOT reintroduce `window.shopify.redirect.dispatch('REMOTE', url)`. That is
 * the App Bridge v2/v3 shape; index.html loads the v4 CDN bundle, whose global
 * has no `redirect` member at all, so the call throws TypeError. It survived
 * review because the old `if (window.shopify)` test was true in BOTH the working
 * and the broken case, and because a test stubbed `redirect` and then asserted
 * against its own stub.
 */
export function redirectTopFrame(url: string): void {
  window.open(url, "_top");
}
